import { Injectable, computed, inject, signal } from '@angular/core';
import { ModulesStore } from 'app/features/admin/modules/modules.store';
import { cloneDeep } from 'lodash';

export interface FieldReference {
  type: 'action' | 'column' | 'interface-action' | 'filter' | 'widget' | 'field';
  id: string;
  name: string;
  location: string;
}

// Types of objects to check
type CheckContext = {
  type: FieldReference['type'];
  path: string; // Path where the item was found (for reference)
  parentName?: string; // Name of the parent (for better location description)
};

@Injectable({
  providedIn: 'root',
})
export class FieldReferenceService {
  private modulesStore = inject(ModulesStore);
  moduleId = signal('');
  module = computed(() => this.modulesStore.getModuleById(this.moduleId()));

  actions = computed(() => {
    return this.modulesStore.actions()[this.moduleId()] || [];
  });

  widgets = computed(() => {
    return cloneDeep(this.module()?.widgets) || [];
  });

  interfaceActions = computed(() => {
    return cloneDeep(this.module()?.interfaceActions) || [];
  });

  columns = computed(() => {
    return cloneDeep(this.module()?.columns) || [];
  });

  filters = computed(() => {
    return cloneDeep(this.module()?.filters) || {};
  });

  filterOptions = computed(() => {
    return cloneDeep(this.module()?.generatedFilterOptions) || [];
  });
  /**
   * Check if a field with a specific keyPath is referenced anywhere in the module
   * @param moduleId Module ID containing the field
   * @param keyPath The keyPath of the field to check
   * @returns Object with reference information
   */
  checkFieldReferencesByKeyPath(
    moduleId: string,
    keyPath: string
  ): {
    referenced: boolean;
    references: FieldReference[];
  } {
    this.moduleId.set(moduleId);
    const references: FieldReference[] = [];
    const moduleData = this.module();

    // Set to track types where references were found (to avoid duplicates)
    const referencedTypes = new Set<FieldReference['type']>();

    if (!moduleData || !keyPath) {
      return { referenced: false, references: [] };
    }

    // Define a data source type
    interface DataSource {
      type: FieldReference['type'];
      data: any;
    }

    // Define all data sources to check
    const dataSources: DataSource[] = [
      { type: 'action', data: this.actions() },
      { type: 'column', data: this.columns() },
      { type: 'interface-action', data: this.interfaceActions() },
      { type: 'widget', data: this.widgets() },
      { type: 'filter', data: { ...this.filters(), options: this.filterOptions() } },
    ];

    // Search for references in each data source
    for (const source of dataSources) {
      const hasReferences = this.searchForReferences(
        source.data,
        keyPath,
        { type: source.type, path: source.type },
        references,
        referencedTypes
      );

      // If references found, add a generic reference entry for this type
      if (hasReferences && !referencedTypes.has(source.type)) {
        references.push(this.createGenericReference(source.type));
        referencedTypes.add(source.type);
      }
    }

    return {
      referenced: references.length > 0,
      references,
    };
  }

  /**
   * Recursively search for keyPath references in an object or array
   * @param data Data to search
   * @param keyPath KeyPath to find
   * @param context Context of the current search
   * @param references Array to collect references
   * @param referencedTypes Set of types that have been referenced
   * @param parentPath Optional parent path for nested structures
   * @returns Boolean indicating if any references were found
   */
  private searchForReferences(
    data: any,
    keyPath: string,
    context: CheckContext,
    references: FieldReference[],
    referencedTypes: Set<FieldReference['type']>,
    parentPath: string = ''
  ): boolean {
    if (!data) return false;

    // Flag to track if references were found
    let foundReference = false;

    // Special case for direct string match
    if (typeof data === 'string' && data === keyPath) {
      return true; // Found a reference
    }

    // Handle arrays
    if (Array.isArray(data)) {
      for (let index = 0; index < data.length; index++) {
        const item = data[index];
        const itemPath = `${parentPath}[${index}]`;
        const itemContext = { ...context, path: `${context.path}.${index}` };

        // Extract name from the item for better reference descriptions
        if (item && typeof item === 'object') {
          if ('name' in item) {
            itemContext.parentName = item.name || undefined;
          } else if ('columnName' in item) {
            itemContext.parentName = item.columnName || undefined;
          }
        }

        if (this.searchForReferences(item, keyPath, itemContext, references, referencedTypes, itemPath)) {
          foundReference = true;
        }
      }
      return foundReference;
    }

    // Handle objects
    if (data && typeof data === 'object') {
      // Handle specific field.key cases (which can be string/number/array)
      if (
        context.type === 'column' &&
        'field' in data &&
        data.field &&
        typeof data.field === 'object' &&
        'key' in data.field
      ) {
        const key = data.field.key;
        const keyString =
          typeof key === 'string'
            ? key
            : typeof key === 'number'
              ? key.toString()
              : Array.isArray(key)
                ? key.join('.')
                : '';

        if (keyString === keyPath) {
          return true; // Found a reference
        }
      }

      // Process each property
      for (const prop in data) {
        if (prop === keyPath) {
          foundReference = true;
        }

        const value = data[prop];
        const propPath = parentPath ? `${parentPath}.${prop}` : prop;
        const propContext = { ...context, path: `${context.path}.${prop}` };

        // Check if this property exactly matches keyPath
        if (typeof value === 'string' && value === keyPath) {
          foundReference = true;
        }
        // Recursively check nested structures
        else if (value !== null && typeof value === 'object') {
          if (this.searchForReferences(value, keyPath, propContext, references, referencedTypes, propPath)) {
            foundReference = true;
          }
        }
      }
    }

    return foundReference;
  }

  /**
   * Create a generic reference entry for a type
   */
  private createGenericReference(type: FieldReference['type']): FieldReference {
    const formattedType = this.formatTypeName(type);

    return {
      type: type,
      id: '',
      name: formattedType,
      location: `- ${formattedType}`,
    };
  }

  /**
   * Format the type name for better readability
   */
  private formatTypeName(type: string): string {
    switch (type) {
      case 'interface-action':
        return 'Interfaceacties';
      case 'action':
        return 'Acties';
      case 'column':
        return 'Kolommen';
      case 'widget':
        return 'Widgets';
      case 'filter':
        return 'Filters';
      case 'field':
        return 'Velden';
      default:
        return type.charAt(0).toUpperCase() + type.slice(1) + 's';
    }
  }
}
